import has from 'lodash/has';
import extend from 'lodash/extend';
import { localForage } from '@Utils/Storage';
import { version } from '@Constants/BuildInfo';
import uuid from 'uuid/v4';
import { DbStorageErrorsModal } from '@Services/AppDBStorage/constants';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
/* eslint-disable class-methods-use-this */
/* eslint-disable  consistent-return */
// eslint-disable-next-line import/prefer-default-export
export class AppDBStorage {
  static globalDBStorage = new AppDBStorage();

  constructor(ns) {
    this._ns = ns;
    this._isErrorShown = false;
  }

  getDefaultValue() {
    return null;
  }

  async set(key, value) {
    return this._set(key, value);
  }

  async get(key) {
    return this._get(key);
  }

  async setNew(value) {
    const id = this.uniqId();
    try {
      await this._set(id, value);
      return id;
    } catch (e) {
      Logger.reject(e);
    }
  }

  async update(key, fn) {
    return this._change(key, fn);
  }

  async remove(key) {
    const fullKey = `${this._ns}${key}`;
    return localForage.removeItem(fullKey);
  }

  async clear() {
    return this._each(
      (value, key) => {
        this.remove(key);
      },
      { ignoreItemParser: true }
    );
  }

  async cleanUp() {
    return this._each(
      (value, key) => {
        const parsed = this._unserialize(value);
        if (parsed?.v !== version) {
          this.remove(key);
        }
      },
      { ignoreItemParser: true }
    );
  }

  async getAll() {
    try {
      const result = [];
      await this._each((value) => {
        result.push(value);
      });
      return result;
    } catch (e) {
      Logger.reject(e);
    }
  }

  uniqId() {
    return uuid();
  }

  async _set(key, value) {
    if (key === undefined) {
      Logger.error("AppDBStorage: Can't set item without a key.");
      return undefined;
    }

    const fullKey = `${this._ns}${key}`;
    const dataToStore = this._serialize({ c: value, v: version });

    try {
      const result = await localForage.setItem(fullKey, dataToStore);
      return result;
    } catch (e) {
      if (has(DbStorageErrorsModal, e.name)) {
        if (!this._isErrorShown) {
          const modalOpts = extend({}, DbStorageErrorsModal[e.name], {
            onConfirm: () => {
              this._isErrorShown = false;
            },
            trackingOptions: {
              message: `${DbStorageErrorsModal.title} ${DbStorageErrorsModal.text}`,
              component: 'Database storage',
            },
          });

          NotifyDialogsService.openModalWindow(modalOpts);
          this._isErrorShown = true;
        }
      } else {
        Logger.error('Unknown error in localforage.setItem:', e);
      }
    }
  }

  _serialize(item) {
    return JSON.stringify(item);
  }

  _unserialize(serializedItem) {
    if (serializedItem && typeof serializedItem === 'string') {
      try {
        return JSON.parse(serializedItem);
      } catch (e) {
        Logger.warn('AppDBStorage: Failed to parse serialized data:', e);
        return null;
      }
    }
    return serializedItem;
  }

  async _get(key) {
    if (key === undefined) {
      Logger.warn("AppDBStorage: Can't get item without a key.");
      return this.getDefaultValue();
    }

    const fullKey = `${this._ns}${key}`;
    try {
      const serializedValue = await localForage.getItem(fullKey);
      const value = this._unserialize(serializedValue);
      if (value?.v === version) {
        return value?.c == null ? this.getDefaultValue() : value.c;
      }
      return this.getDefaultValue();
    } catch (e) {
      Logger.warn('AppDBStorage: Failed to retrieve from storage:', e);
      return this.getDefaultValue();
    }
  }

  async _change(key, fn) {
    try {
      const item = await this._get(key);
      const result = fn(item);
      await this._set(key, result);
      return result;
    } catch (e) {
      Logger.reject(e);
    }
  }

  async _each(callback, options) {
    const ignoreParser = !!options?.ignoreItemParser;
    return localForage.iterate((value, key) => {
      if (key.indexOf(this._ns) === 0) {
        // eslint-disable-next-line no-param-reassign
        value = ignoreParser ? value : this._unserialize(value)?.c;
        return callback(value, key.replace(this._ns, ''));
      }
    });
  }
}
